import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Animation d'entrée
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PortfolioSection = styled.section`
  padding: 7.2rem 5%;
  background-color: ${props => props.theme.colors.background};
  position: relative;
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  text-align: center;
  margin-bottom: 0.9rem;
  position: relative;
  z-index: 1;
  color: ${props => props.theme.colors.text};
  
  &::after {
    content: '';
    display: block;
    width: 72px;
    height: 3.6px;
    background: ${props => props.theme.colors.accentGradient};
    margin: 0.9rem auto;
    border-radius: 1.8px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 630px;
  margin: 0 auto 3.6rem;
  color: ${props => props.theme.colors.secondaryText};
  position: relative;
  z-index: 1;
  line-height: 1.6;
`;

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(315px, 1fr));
  gap: 1.8rem;
  margin-top: 3.6rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Utilisation de la balise article pour les projets (meilleure sémantique)
const ProjectCard = styled.article`
  border-radius: 13.5px;
  overflow: hidden;
  position: relative;
  height: 315px;
  animation: ${fadeIn} 0.5s ease-out;
  animation-fill-mode: both;
  animation-delay: ${props => props.delay || '0s'};
  box-shadow: 0 9px 27px rgba(0, 0, 0, ${props => props.theme.name === 'light' ? '0.1' : '0.3'});
  transition: transform 0.5s ease, filter 0.5s ease; /* Ajout de la transition pour le filtre */
  &:hover .overlay {
    opacity: 1;
  }

  &:hover img {
  transform: scale(1.1);
  filter: blur(3px); /* Ajout de l'effet de flou lors du survol */
}
  
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    
    &:hover img {
      transform: none;
    }
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ProjectOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, 
    ${props => props.theme.name === 'light'
    ? 'rgba(0, 0, 0, 0.8)'  /* Amélioré pour un meilleur contraste */
    : 'rgba(0, 0, 0, 0.9)'}, 
    transparent);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.8rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    opacity: 1; /* Toujours visible pour réduire les mouvements */
    background: linear-gradient(to top, 
      ${props => props.theme.name === 'light'
    ? 'rgba(0, 0, 0, 0.7)'
    : 'rgba(0, 0, 0, 0.8)'}, 
      transparent 80%);
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1.35rem;
  margin-bottom: 0.45rem;
  color: white; /* Meilleur contraste sur l'overlay foncé */
`;

const ProjectCategory = styled.span`
  font-size: 0.81rem;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.9rem;
  font-weight: 600;
`;

const ProjectDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.9rem;
  font-size: 0.855rem;
  line-height: 1.5;
`;

const ProjectLink = styled.a`
  display: inline-block;
  padding: 0.63rem 1.35rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border-radius: 45px;
  font-weight: 600;
  font-size: 0.81rem;
  transition: all 0.3s ease;
  text-decoration: none;
  
  &:hover, &:focus {
    background: ${props => props.theme.name === 'light' ? '#8C52FF' : '#8C52FF'};
    color: white;
    transform: translateY(-2.7px);
    box-shadow: 0 4.5px 13.5px rgba(0, 0, 0, 0.2);
  }
  
  &:focus {
    outline: 2.7px solid white;
    outline-offset: 1.8px;
  }
  
  &:focus:not(:focus-visible) {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2.7px solid white;
    outline-offset: 1.8px;
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    
    &:hover, &:focus {
      transform: none;
    }
  }
`;

// Composant pour les liens "Voir plus"
const ViewMoreContainer = styled.div`
  text-align: center;
  margin-top: 3.6rem;
`;

const ViewMoreButton = styled.button`
  display: inline-block;
  padding: 0.9rem 2.25rem;
  background: transparent;
  color: ${props => props.theme.colors.accent};
  border: 1.8px solid ${props => props.theme.colors.accent};
  border-radius: 45px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover, &:focus {
    background: rgba(94, 23, 235, 0.1);
    transform: translateY(-2.7px);
    box-shadow: 0 4.5px 13.5px rgba(94, 23, 235, 0.2);
  }

  &:focus {
    outline: 2.7px solid ${props => props.theme.colors.accent};
    outline-offset: 1.8px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover, &:focus {
      transform: none;
    }
  }
`;

const Portfolio = () => {
  const [showAll, setShowAll] = useState(false);

  // Données des projets enrichies avec des descriptions
  const projects = [
    {
      id: "winera",
      link: "https://evodynapp.com/",
      title: "Application Web Winera",
      category: "Application web",
      description: "Application web de gestion de clients pour coachs sportifs. Planification, suivi et optimisation de l'activité en un seul outil.",
      image: "winera.webp",
      alt: "Interface de l'application Winera pour la gestion de clients de coaching sportif"
    },
    {
      id: "todotoday",
      link: "https://todotoday.site/",
      title: "TodoToday - Gestionnaire de tâches",
      category: "Application web",
      description: "Application de gestion de tâches intuitive et performante pour améliorer votre productivité quotidienne.",
      image: "todotoday.png",
      alt: "Interface de TodoToday avec liste de tâches organisée"
    },
    {
      id: "draftpad",
      link: "https://draftpad.site/",
      title: "Draftpad - Tableau blanc collaboratif",
      category: "Application web",
      description: "Espace de travail collaboratif en temps réel pour la création visuelle et le brainstorming d'équipe.",
      image: "draftpad.png",
      alt: "Interface de Draftpad avec tableau blanc collaboratif et outils de dessin"
    },
    {
      id: "caroline_caumon",
      link: "https://carolinecaumonmassagemethode.com",
      title: "Site vitrine Caroline Caumon",
      category: "Création de site",
      description: "Conception d'un site responsive moderne pour vente de formation de massage bien-être.",
      image: "caroline_caumon.png",
      alt: "Page d'accueil du site Caroline Caumon avec design élégant et professionnel"
    },
    {
      id: "decko",
      title: "Refonte Site Web",
      link: "https://decko.fr",
      category: "Refonte site web",
      description: "Modernisation complète du site d'aménagement de terrasse avec amélioration de l'UX et optimisation des performances.",
      image: "decko.png",
      alt: "Version revisitée du site Decko"
    },
    {
      id: "jojowork",
      link: "https://www.jojowork.fr/",
      title: "Jojo Work - Outil de productivité",
      category: "Application web",
      description: "Outil qui simplifie votre façon de travailler. Restez concentré, organisez vos tâches et gérez votre temps efficacement avec une interface minimaliste.",
      image: "jojowork.png",
      alt: "Interface de Jojo Work avec gestion de tâches et organisation du temps"
    },
  ];

  // Afficher seulement 4 projets par défaut
  const displayedProjects = showAll ? projects : projects.slice(0, 4);
  const hasMoreProjects = projects.length > 4;

  return (
    <PortfolioSection id="portfolio" aria-labelledby="portfolio-title">
      <SectionTitle id="portfolio-title">Notre Portfolio</SectionTitle>
      <SectionSubtitle>
        Découvrez nos dernières réalisations et comment nous avons aidé nos clients à développer leur présence en ligne avec des sites web professionnels et attractifs.
      </SectionSubtitle>

      <PortfolioGrid>
        {displayedProjects.map((project, index) => (
          <ProjectCard
            key={project.id}
            delay={`${index * 0.2}s`}
          >
            <ProjectImage
              src={project.image}
              alt={project.alt}
              loading={index > 1 ? "lazy" : "eager"} // Lazy loading pour les images non visibles immédiatement
            />
            <ProjectOverlay className="overlay">
              {/*<ProjectCategory>{project.category}</ProjectCategory>*/}
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              <ProjectLink
                href={`${project.link}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Voir les détails du projet ${project.title}`}
              >
                Voir le projet
              </ProjectLink>
            </ProjectOverlay>
          </ProjectCard>
        ))}
      </PortfolioGrid>

      {hasMoreProjects && (
        <ViewMoreContainer>
          <ViewMoreButton
            onClick={() => setShowAll(!showAll)}
            aria-label={showAll ? "Voir moins de projets" : "Voir plus de projets"}
          >
            {showAll ? "Voir moins" : `Voir plus de projets (${projects.length - 4})`}
          </ViewMoreButton>
        </ViewMoreContainer>
      )}
    </PortfolioSection>
  );
};

export default Portfolio;